
import dataTestIds from 'app/cypress/e2e/dataTestIds';
import { Platform } from 'quasar';
import AppIcon from 'src/assets/AppIcon.vue';
import useAnimations from 'src/composables/animations';
import useEmitter from 'src/composables/emitter';
import useMenu from 'src/composables/menu';
import { getRoutePath, ROUTES } from 'src/router/routes';
import {
  getCurrentWeather,
  IWeatherResponse,
} from 'src/services/weatherService';
import { useUserStore } from 'src/store/userStore';
import { computed, defineComponent, onMounted, ref, watch } from 'vue';

import PointsIndicator from './PointsIndicator.vue';

export const PAWS_MAP = [
  'paw-first',
  'paw-second',
  'paw-third',
  'paw-last',
  'paw-mid',
];

export default defineComponent({
  name: 'PageHeader',
  components: {
    PointsIndicator,
    AppIcon,
  },
  props: {
    showLogo: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['showDrawer'],
  setup() {
    const user = useUserStore();
    const { emitter } = useEmitter();
    const { headerLinks } = useMenu();
    const { fadeElement } = useAnimations();

    const weatherData = ref<IWeatherResponse | null>();
    const loadingWeatherData = ref();
    const timeData = ref();
    const points = computed(() => {
      const formatter = Intl.NumberFormat('en', { notation: 'compact' });
      return formatter.format(user.points);
    });

    const pointsRaw = computed(() => {
      return user.points;
    });

    function sleep(ms: number) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    }

    const handlePawColor = async () => {
      if (user.points >= 5) {
        fadeElement(
          <HTMLElement & { faded: boolean }>document.getElementById(PAWS_MAP[0])
        );
      }
      if (user.points >= 15) {
        fadeElement(
          <HTMLElement & { faded: boolean }>document.getElementById(PAWS_MAP[1])
        );
      }
      if (user.points >= 30) {
        fadeElement(
          <HTMLElement & { faded: boolean }>document.getElementById(PAWS_MAP[2])
        );
      }
      if (user.points >= 40) {
        fadeElement(
          <HTMLElement & { faded: boolean }>document.getElementById(PAWS_MAP[3])
        );
      }
      await sleep(1000);
      if (user.points >= 45) {
        fadeElement(
          <HTMLElement & { faded: boolean }>(
            document.getElementById(PAWS_MAP[4])
          ),
          true
        );
      }
    };

    watch(
      pointsRaw,
      () => {
        void handlePawColor();
      },
      { deep: true, immediate: true }
    );

    const weatherDataString = computed(() => {
      if (!weatherData?.value) return;

      return `${weatherData.value.location.name}, ${weatherData.value.current.temp_c} <sup>o</sup>C, ${weatherData.value.current.condition.text}`;
    });

    function formatAMPM(date = new Date()) {
      let hours = date.getHours();
      let minutes = date.getMinutes();
      let ampm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      let minutesStr = minutes < 10 ? '0' + minutes : minutes;
      return hours + ':' + minutesStr + ' ' + ampm;
    }

    function getOrdinalNum(n: number) {
      return (
        n +
        (n > 0
          ? ['th', 'st', 'nd', 'rd'][
              (n > 3 && n < 21) || n % 10 > 3 ? 0 : n % 10
            ]
          : '')
      );
    }

    onMounted(() => {
      if (Platform.is.capacitor) {
        async function setWeatherData() {
          try {
            loadingWeatherData.value = true;
            weatherData.value = (await getCurrentWeather()).data;
          } catch {
            weatherData.value = null;
          } finally {
            loadingWeatherData.value = false;
          }
        }
        void setWeatherData();

        // Fetch up-to-date weather data every 5 minutes
        setInterval(() => void setWeatherData(), 5 * 60 * 1000);

        // Update the date/time each second
        function updateTimeData() {
          const date = new Date();

          timeData.value = `${formatAMPM()}, ${date.toLocaleString('en-us', {
            weekday: 'long',
          })} ${getOrdinalNum(date.getDate())} ${date.toLocaleString(
            'default',
            {
              month: 'long',
            }
          )}, ${date.getFullYear()}`;
        }
        updateTimeData();
        setInterval(() => {
          updateTimeData();
        }, 1000);
      }

      emitter.on('color-paw', () => {
        void handlePawColor();
      });
    });

    return {
      points,
      headerLinks,
      getRoutePath,
      ROUTES,
      loadingWeatherData,
      weatherDataString,
      timeData,
    };
  },
  computed: {
    dataTestIds() {
      return dataTestIds;
    },
  },
});
